import React, { useCallback, useEffect, useState } from 'react';
import { Search, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';
import { portalFetch } from '../shared/api';
import { AccessEntry, areaLabel, actorLabel, actionLabel, whenLabel } from '../../components/accessLogText';

// ============================================================
// Access log in the in-app admin console: which staff member opened or
// changed which investor's data (Backend/services/staffAccessLog.js). The
// same entries each investor sees about themselves in the Trust Center —
// management sees them across all investors, or filtered to one.
// Monitoring only, in line with the console's scope.
// ============================================================

const AdminAccessLog: React.FC = () => {
  const { language } = useLanguage();
  const de = language === 'de';
  const [entries, setEntries] = useState<AccessEntry[]>([]);
  const [filter, setFilter] = useState('');
  const [applied, setApplied] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (investorId: string) => {
    setLoading(true);
    setError(null);
    try {
      const q = investorId ? `?investorId=${encodeURIComponent(investorId)}` : '';
      const r = await portalFetch<{ success: boolean; entries: AccessEntry[] }>(`/admin/staff-access-log${q}`);
      setEntries(r.entries || []);
    } catch (e: any) {
      setError(e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(applied); }, [load, applied]);

  return (
    <div style={{ padding: '14px', fontFamily: 'Montserrat, sans-serif', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 17, fontWeight: 800 }}>
        <ShieldCheck size={20} color="#D6B160" /> {de ? 'Zugriffsprotokoll' : 'Access log'}
      </div>
      <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.6)', marginTop: 4, lineHeight: 1.45 }}>
        {de
          ? 'Wer aus dem Team welche Investorendaten geöffnet oder geändert hat. Jeder Investor sieht seine eigenen Einträge im Trust Center.'
          : 'Which team member opened or changed which investor data. Each investor sees their own entries in the Trust Center.'}
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); setApplied(filter.trim().toLowerCase()); }}
        style={{ display: 'flex', gap: 8, marginTop: 12 }}
      >
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 10, background: '#f3f4f6' }}>
          <Search size={16} color="#9ca3af" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={de ? 'Investor-E-Mail (leer = alle)' : 'Investor e-mail (empty = all)'}
            style={{ border: 'none', background: 'transparent', outline: 'none', flex: 1, fontSize: 14, fontFamily: 'inherit' }}
          />
        </div>
        <button type="submit" style={{ padding: '9px 16px', borderRadius: 999, border: 'none', background: '#000', color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          {de ? 'Anzeigen' : 'Show'}
        </button>
      </form>

      <div style={{ marginTop: 12 }}>
        {loading ? (
          <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.55)' }}>…</div>
        ) : error ? (
          <div style={{ fontSize: 13, color: '#000' }}>{de ? 'Konnte nicht geladen werden:' : 'Could not load:'} {error}</div>
        ) : entries.length === 0 ? (
          <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.55)' }}>{de ? 'Noch keine Einträge.' : 'No entries yet.'}</div>
        ) : entries.map((e, i) => (
          <div key={`${e.at}-${i}`} style={{ padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.08)', fontSize: 13.5 }}>
            <div style={{ fontWeight: 700 }}>
              {actorLabel(e, de)} <span style={{ fontWeight: 500, color: 'rgba(0,0,0,0.6)' }}>{actionLabel(e, de)}</span> {areaLabel(e.area, de)}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', marginTop: 2 }}>
              {de ? 'Investor' : 'Investor'}: {e.investorId} · {whenLabel(e.at, de)}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'rgba(214,177,96,0.14)', fontSize: 12.5, lineHeight: 1.5 }}>
        {de
          ? 'Einträge mit „gemeinsamer Zugang“ stammen aus der Admin-Konsole mit dem gemeinsamen Passwort und lassen sich keiner Person zuordnen. Persönliche Admin-Zugänge würden jeden Eintrag einem Namen zuordnen.'
          : 'Entries marked "shared login" come from the admin console\'s shared password and cannot be tied to a person. Personal admin logins would put a name on every entry.'}
      </div>
    </div>
  );
};

export default AdminAccessLog;
